import type { ErrorType } from '@cocoonspace/sdk-js/axios-client/use-custom-instance'
import type { InvalidResponse } from '@cocoonspace/sdk-js/types/invalidResponse'
import type { ListSpacesParams } from '@cocoonspace/sdk-js/types/listSpacesParams'
import type { SpacesAlternatives } from '@cocoonspace/sdk-js/types/spacesAlternatives'
import type { UnauthorizedResponse } from '@cocoonspace/sdk-js/types/unauthorizedResponse'
import type { UnexpectedResponse } from '@cocoonspace/sdk-js/types/unexpectedResponse'
import type { Zone } from '@cocoonspace/sdk-js/types/zone'
import {
	createContext,
	type Dispatch,
	type PropsWithChildren,
	type SetStateAction,
	useContext,
	useEffect,
	useMemo,
	useState,
} from 'react'
import {
	type listSpaces,
	useListSpaces,
} from '@cocoonspace/sdk-js/domains/spaces/spaces'
import { dateTimeUtils } from '@cocoonspace/shared/utils/date-time-utils'
import { getCleanObject } from '@cocoonspace/shared/utils/get-clean-object'
import { keepPreviousData, type UseQueryResult } from '@tanstack/react-query'

import { select } from '~/domains/search/config/select-api-keys'
import { useSearchFilters } from '../components/filters/use-search-filters.hook'

interface UserProviderProps {
	zone: Zone
}

export interface SearchContextType extends ReturnType<typeof useSearchFilters> {
	activeAlternative?: string
	setActiveAlternative: Dispatch<SetStateAction<string | undefined>>
	spacesListQuery: UseQueryResult<
		Awaited<ReturnType<typeof listSpaces>>,
		ErrorType<InvalidResponse | UnauthorizedResponse | UnexpectedResponse>
	>
	unfilteredSpacesListQuery: UseQueryResult<
		Awaited<ReturnType<typeof listSpaces>>,
		ErrorType<InvalidResponse | UnauthorizedResponse | UnexpectedResponse>
	>
	hasAlternatives: boolean
	hasNoResults: boolean
	zone: Zone
}

export const SearchContext = createContext<SearchContextType>(
	{} as SearchContextType,
)

export const useSearchSpaces = () => useContext(SearchContext)

const isSpaceInBbox = (
	space: { latitude: number; longitude: number },
	bbox: {
		ne_latitude: number
		ne_longitude: number
		sw_latitude: number
		sw_longitude: number
	},
) => {
	const { latitude, longitude } = space
	const { ne_latitude, ne_longitude, sw_latitude, sw_longitude } = bbox

	return (
		latitude >= sw_latitude &&
		latitude <= ne_latitude &&
		longitude >= sw_longitude &&
		longitude <= ne_longitude
	)
}

interface BboxFilters {
	ne_latitude?: number | null
	ne_longitude?: number | null
	sw_latitude?: number | null
	sw_longitude?: number | null
}

const filterSpacesByBbox = (
	spaces: Array<{ latitude: number; longitude: number }> | undefined,
	filters: BboxFilters,
) => {
	const { ne_latitude, ne_longitude, sw_latitude, sw_longitude } = filters

	if (
		!spaces?.length ||
		ne_latitude == null ||
		ne_longitude == null ||
		sw_latitude == null ||
		sw_longitude == null
	) {
		return spaces
	}

	return spaces.filter((space) =>
		isSpaceInBbox(space, {
			ne_latitude,
			ne_longitude,
			sw_latitude,
			sw_longitude,
		}),
	)
}

interface AlternativeConfig {
	label: SpacesAlternatives['label']
	dayOffset: number
	startOffset: number
	endOffset: number
}

const ALTERNATIVE_CONFIGS: AlternativeConfig[] = [
	{ label: '1d_after', dayOffset: 1, startOffset: 0, endOffset: 0 },
	{ label: '1d_before', dayOffset: -1, startOffset: 0, endOffset: 0 },
	{ label: '1h_after', dayOffset: 0, startOffset: 1, endOffset: 1 },
	{ label: '1h_before', dayOffset: 0, startOffset: -1, endOffset: -1 },
]

const computeAlternativeParams = (
	filters: ListSpacesParams,
	config: AlternativeConfig,
): ListSpacesParams | null => {
	if (!filters.day || !filters.start || !filters.end) return null

	const day = dateTimeUtils(filters.day[0], 'YYYY-MM-DD').add(
		config.dayOffset,
		'day',
	)

	const start = dateTimeUtils(filters.start, 'HH:mm').add(
		config.startOffset,
		'hour',
	)

	const end = dateTimeUtils(filters.end, 'HH:mm').add(config.endOffset, 'hour')

	return {
		...filters,
		day: [day.format('YYYY-MM-DD')],
		start: start.format('HH:mm'),
		end: end.format('HH:mm'),
	}
}

export const SearchProvider = ({
	zone,
	children,
}: PropsWithChildren<UserProviderProps>) => {
	const queryState = useSearchFilters()

	const filtersForApi = useMemo(() => {
		return getCleanObject({
			features: queryState.filters.features,
			capacity: queryState.filters.capacity,
			day: queryState.filters.day,
			end: queryState.filters.end,
			start: queryState.filters.start,
			text: queryState.filters.text,
		}) as ListSpacesParams
	}, [queryState.filters])

	const spacesListQuery = useListSpaces(
		{
			select,
			zone,
			...filtersForApi,
		},
		{ query: { placeholderData: keepPreviousData } },
	)

	const mainResultsCount = useMemo(() => {
		if (!spacesListQuery.data?.data) return undefined
		const data = spacesListQuery.data.data
		const filtered = filterSpacesByBbox(data, queryState.filters)
		return filtered?.length ?? 0
	}, [spacesListQuery.data, queryState.filters])

	const shouldFetchAlternatives =
		mainResultsCount !== undefined &&
		mainResultsCount <= 2 &&
		!!queryState.filters.day?.length &&
		!!queryState.filters.start &&
		!!queryState.filters.end

	const alternativeQueries = ALTERNATIVE_CONFIGS.map((config) => {
		const altFilters = computeAlternativeParams(filtersForApi, config)

		return useListSpaces(
			{
				select,
				zone,
				...altFilters,
			},
			{
				query: {
					placeholderData: keepPreviousData,
					enabled: shouldFetchAlternatives,
				},
			},
		)
	})

	useEffect(() => {
		if (spacesListQuery.isFetched) {
			const filters = getCleanObject(queryState.filters)
			const isMapVisible = queryState.isMapVisible
			const results = spacesListQuery.data?.data.length || 0
			const alternatives = spacesListQuery.data?.meta?.alternatives?.length || 0

			const props = {
				zone,
				...filters,
				alternatives,
				isMapVisible,
				results,
			}

			if (typeof window !== 'undefined' && window.umami) {
				window.umami.track('search_results', props as any)
			}
		}
		// oxlint-disable-next-line eslint-plugin-react-hooks/exhaustive-deps
	}, [spacesListQuery.isFetched])

	const [activeAlternative, setActiveAlternative] = useState<
		string | undefined
	>()

	const clientAlternatives = useMemo((): SpacesAlternatives[] => {
		if (!shouldFetchAlternatives) return []

		return ALTERNATIVE_CONFIGS.map((config, index) => {
			const query = alternativeQueries[index]
			const data = filterSpacesByBbox(query.data?.data, queryState.filters)
			const altFilters = computeAlternativeParams(filtersForApi, config)

			return {
				label: config.label,
				day: altFilters?.day?.[0] || '',
				start: altFilters?.start || '',
				end: altFilters?.end || '',
				data: (data || []) as SpacesAlternatives['data'],
			}
		}).filter((alt) => alt.data.length > 0)
	}, [
		shouldFetchAlternatives,
		alternativeQueries,
		queryState.filters,
		filtersForApi,
	])

	const filteredData = useMemo(() => {
		if (!spacesListQuery.data) return undefined

		const filtered = filterSpacesByBbox(
			spacesListQuery.data.data,
			queryState.filters,
		)

		const apiAlternatives = spacesListQuery.data.meta?.alternatives || []

		const allAlternatives =
			clientAlternatives.length > 0 ? clientAlternatives : apiAlternatives

		return {
			...spacesListQuery.data,
			data: filtered as typeof spacesListQuery.data.data,
			meta: {
				...spacesListQuery.data.meta,
				alternatives: allAlternatives.map((alt) => ({
					...alt,
					data: filterSpacesByBbox(
						alt.data,
						queryState.filters,
					) as typeof alt.data,
				})),
			},
		}
	}, [spacesListQuery.data, queryState.filters, clientAlternatives])

	const filteredSpacesListQuery = useMemo(
		() => ({
			...spacesListQuery,
			data: filteredData,
		}),
		[spacesListQuery, filteredData],
	)

	const hasAlternatives = !!filteredData?.meta?.alternatives?.length

	const hasNoResults =
		!spacesListQuery.isLoading && !filteredData?.data.length && !hasAlternatives

	const value = useMemo(
		() => ({
			...queryState,
			activeAlternative,
			hasAlternatives,
			hasNoResults,
			setActiveAlternative,
			spacesListQuery:
				filteredSpacesListQuery as SearchContextType['spacesListQuery'],
			unfilteredSpacesListQuery:
				spacesListQuery as SearchContextType['unfilteredSpacesListQuery'],
			zone,
		}),
		[
			queryState,
			activeAlternative,
			hasAlternatives,
			hasNoResults,
			filteredSpacesListQuery,
			spacesListQuery,
			zone,
		],
	)

	return (
		<SearchContext.Provider value={value}>{children}</SearchContext.Provider>
	)
}
